import DomHandler from '../components/utils/DomHandler';

export default ({ app, $dayjs, store, i18n, localePath, $device }, inject) => {
    inject('clone', (obj) => {
        return JSON.parse(JSON.stringify(obj));
    });
    inject('scrollToElement', (selector, offset = 150, mode = 'smooth') => {
        const $element = document.querySelector(selector);

        if (!$element) {
            return;
        }

        const targetScrollY =
            $element.getBoundingClientRect().top -
            store.getters['data/getPublicAnnouncementsToDisplay'].length * 16 * 3 +
            window.scrollY -
            offset;

        if ('scrollBehavior' in document.documentElement.style) {
            window.scrollTo({ left: 0, top: targetScrollY, behavior: mode });
        } else {
            window.scrollTo(0, targetScrollY);
        }
    });
    inject('downloadFile', (url, fileName) => {
        fetch(url, { method: 'get', referrerPolicy: 'no-referrer' })
            .then((res) => res.blob())
            .then((res) => {
                const aElement = document.createElement('a');
                aElement.setAttribute('download', fileName);
                const href = URL.createObjectURL(res);
                aElement.href = href;
                aElement.setAttribute('target', '_blank');
                aElement.click();
                URL.revokeObjectURL(href);
            });
    });

    // This method was introduced because:
    // 1. the thumbnail image is not always required & the hero image can be used for it as fallback
    // 2. there is no naming convention for the hero & thumbnail image props, they are inconsistent
    inject(
        'getMediaImage',
        (
            media,
            type,
            size = $device.isMobile ? 'sm' : 'md',
            mobileAndDesktopAspectRatios = ['landscape', 'landscape']
        ) => {
            if (!/^(hero|lead|image)$/.test(type)) {
                throw new Error('Invalid media type');
            }

            if (!/^(sm|md|lg)$/.test(size)) {
                throw new Error('Invalid media image size');
            }

            if (
                !Array.isArray(mobileAndDesktopAspectRatios) ||
                mobileAndDesktopAspectRatios.find((ratio) => !/^(landscape|portrait|square|default)$/.test(ratio))
            ) {
                throw new Error('Invalid aspect ratio');
            }

            if (typeof media !== 'object' || Array.isArray(media) || media === null) {
                console.error('Invalid media value type');
                return '';
            }

            let aspectRatio = $device.isMobile ? mobileAndDesktopAspectRatios[0] : mobileAndDesktopAspectRatios[1];

            // convert values to a format that will be served by the api
            if (aspectRatio === 'landscape') {
                aspectRatio = '3_2';
            } else if (aspectRatio === 'portrait') {
                aspectRatio = '3_4';
            } else if (aspectRatio === 'square') {
                aspectRatio = '1_1';
            }

            let source = '/og-image.png';

            if (media.image && type === 'image') {
                source = media.image[size];
            }

            if (media.lead && type === 'lead') {
                source = media.lead[size];
            } else if (media.lead_image && type === 'lead') {
                source = media.lead_image[size];
            } else if (media.hero && /^(lead|hero)$/.test(type)) {
                source = media.hero[size];
            } else if (media.hero_image && /^(lead|hero)$/.test(type)) {
                source = media.hero_image[size];
            } else if (media.header_image && /^(lead|hero)$/.test(type)) {
                source = media.header_image[size];
            }

            // not all images come from the api with alternative aspect ratios,
            // some just come directly as media.hero.sm instead of media.hero.sm.3_2
            if (typeof source === 'string') {
                return source;
            } else if (aspectRatio) {
                return source[aspectRatio];
            } else {
                return source.default;
            }
        }
    );
    inject('generateDateFilterOptions', () => {
        const dayIndex = $dayjs().day();

        return [
            {
                id: 1,
                label: i18n.t('time.any'),
                value: 'any',
            },
            {
                id: 2,
                label: i18n.t('time.today'),
                value: `${$dayjs().format('YYYY-MM-DD')}_${$dayjs().format('YYYY-MM-DD')}`,
            },
            {
                id: 3,
                label: i18n.t('time.tomorrow'),
                value: `${$dayjs().add(1, 'day').format('YYYY-MM-DD')}_${$dayjs().add(1, 'day').format('YYYY-MM-DD')}`,
            },
            // {
            //     id: 4,
            //     label: 'A héten',
            //     value: `${$dayjs().format('YYYY-MM-DD')}_${$dayjs()
            //         .add(7 - dayIndex, 'day')
            //         .format('YYYY-MM-DD')}`,
            // },
            {
                id: 5,
                label: i18n.t('time.this_weekend'),
                value: `${$dayjs()
                    .add(7 - dayIndex - 1, 'day')
                    .format('YYYY-MM-DD')}_${$dayjs()
                    .add(7 - dayIndex, 'day')
                    .format('YYYY-MM-DD')}`,
            },
            // {
            //     id: 6,
            //     label: 'Jövő héten',
            //     value: `${$dayjs()
            //         .add(7 - dayIndex + 1, 'day')
            //         .format('YYYY-MM-DD')}_${$dayjs()
            //         .add(7 - dayIndex + 7, 'day')
            //         .format('YYYY-MM-DD')}`,
            // },
        ];
    });
    inject('getNewsCategoryLabel', (id) => {
        if (!id || !store.state.data.newsCategories.length) {
            return null;
        }
        return store.state.data.newsCategories.find((x) => x.id === id)?.name || '';
    });
    inject('getFormattedEventInterval', (event) => {
        const date = {
            displayed: '',
            accessible: '',
        };

        if (event.text_date_only && event.date) {
            date.displayed = event.date;
            date.accessible = event.date;
            return date;
        }

        if (event.exhibition && event.constant) {
            date.displayed = i18n.t('programs_in_varosliget.constant_exhibition');
            date.accessible = i18n.t('programs_in_varosliget.constant_exhibition');
            return date;
        }

        if (event.exhibition && event.periodic) {
            date.displayed = i18n.t('programs_in_varosliget.periodic_exhibition');
            date.accessible = i18n.t('programs_in_varosliget.periodic_exhibition');
            return date;
        }

        const $startDate = $dayjs(event.start_date);
        const $endDate = $dayjs(event.end_date);

        if ($startDate && !$startDate.isValid()) {
            console.error('Invalid date');
            return null;
        }

        // If event is NOT periodic or constant, we only need to return a single date, which is the start date
        if (!event.constant && !event.periodic) {
            date.accessible += i18n.t('a11y.at_datetime');

            if (!$dayjs().isSame($startDate, 'year')) {
                date.displayed += $startDate.format('YYYY');
                date.accessible += ' ' + $startDate.format('YYYY');
            }

            date.displayed += ' ' + $startDate.format('MMM.').toUpperCase();
            date.displayed += ' ' + $startDate.format('D. ddd.');
            date.accessible += ' ' + $startDate.format('MMMM D dddd');

            if ($startDate.hour() !== 0 || $startDate.minute() !== 0) {
                date.displayed += ' ' + $startDate.format('HH:mm');
                date.accessible += ' ' + $startDate.format('HH:mm');
            }
        } else {
            if (i18n.locale === 'hu') {
                date.accessible += i18n.t('a11y.at_datetime');
            } else {
                date.accessible += i18n.t('a11y.between');
            }

            // Else, we return an interval
            if (!$dayjs().isSame($startDate, 'year')) {
                date.displayed += $startDate.format('YYYY');
                date.accessible += ' ' + $startDate.format('YYYY');
            }

            date.displayed += ' ' + $startDate.format('MMM.').toUpperCase();
            date.displayed += ' ' + $startDate.format('D.');
            date.accessible += ' ' + $startDate.format('MMMM D');
            // if ($startDate.hour() !== 0 || $startDate.minute() !== 0) {
            //     ret += ' ' + $startDate.format('HH:mm');
            // }

            if ($endDate && $endDate.isValid()) {
                date.displayed += ' -';
                date.accessible += i18n.t('a11y.and');

                if (!$dayjs($startDate).isSame($endDate, 'year')) {
                    date.displayed += ' ' + $endDate.format('YYYY');
                    date.accessible += ' ' + $endDate.format('YYYY');
                }

                if (!$dayjs($startDate).isSame($endDate, 'month')) {
                    date.displayed += ' ' + $endDate.format('MMM.').toUpperCase();
                    date.accessible += ' ' + $endDate.format('MMMM');
                }
                if (!$dayjs($startDate).isSame($endDate, 'day')) {
                    date.displayed += ' ' + $endDate.format('D.');
                    date.accessible += ' ' + $endDate.format('D');
                }

                // if ($endDate.hour() !== 0 || $endDate.minute() !== 0) {
                //     ret += ' ' + $endDate.format('HH:mm');
                // }

                if (i18n.locale === 'hu') {
                    date.accessible += i18n.t('a11y.between');
                }
            }
        }

        return date;
    });
    inject('getEventLocationName', (eventLocationID) => {
        return store.state.data.eventLocations.find((x) => x.id === eventLocationID)?.title;
    });
    inject('getEventUrl', (event) => {
        if (event.url) {
            return event.url;
        }

        // if (event.series) {
        //     return localePath({ name: 'program-series-programSeries', params: { programSeries: event.series.slug } });
        // }

        return localePath({ name: 'programs-in-varosliget-program', params: { program: event.slug } });
    });
    inject('getPageTitle', (data) => {
        let _metaData;

        if (typeof data === 'string') {
            _metaData = store.state.data.metaData.find((x) => x.key === data);
        } else {
            _metaData = data;
        }

        if (!_metaData) {
            return null;
        }

        return _metaData.title;
    });
    inject('getValidatedTranslationValueURL', (TKey) => {
        const url = i18n.t(TKey);

        if (url && /^https:\/\//.test(url)) {
            return url;
        }

        return null;
    });
    inject('getPageMetaData', (data) => {
        let _metaData;

        if (typeof data === 'string') {
            _metaData = store.state.data.metaData.find((x) => x.key === data);
        } else {
            _metaData = data;
        }

        if (!_metaData) {
            return [];
        }

        const ret = [];

        if (_metaData.title) {
            ret.push({ hid: 'title', name: 'title', content: _metaData.title });
            ret.push({ hid: 'og:title', name: 'og:title', content: _metaData.title });
        }

        if (_metaData.description) {
            ret.push({ hid: 'description', name: 'description', content: _metaData.description });
            ret.push({ hid: 'og:description', name: 'og:description', content: _metaData.description });
        }

        if (_metaData.image) {
            ret.push({ hid: 'og:image', name: 'og:image', content: _metaData.image });
            ret.push({ hid: 'og:image:url', name: 'og:image:url', content: _metaData.image });
        }

        return ret;
    });
    inject('trapFocus', (event, wrapper) => {
        event.preventDefault();
        const focusableElements = DomHandler.getFocusableElements(wrapper);

        if (!document.activeElement) {
            focusableElements[0].focus();
        } else {
            const focusedIndex = focusableElements.indexOf(document.activeElement);

            if (event.shiftKey) {
                if (focusedIndex === -1 || focusedIndex === 0) focusableElements[focusableElements.length - 1].focus();
                else focusableElements[focusedIndex - 1].focus();
            } else {
                // eslint-disable-next-line
                if (focusedIndex === -1 || focusedIndex === focusableElements.length - 1) {
                    focusableElements[0].focus();
                } else {
                    focusableElements[focusedIndex + 1].focus();
                }
            }
        }
    });
    inject('generateGoogleCalendarUrl', ({ title, details, location, startDate, endDate }) => {
        const formatDateForGoogle = (date) =>
            $dayjs(date).toISOString().replaceAll(':', '').replaceAll('-', '').replaceAll('.000', '');

        let url = 'https://www.google.com/calendar/render?action=TEMPLATE';

        url += `&text=${encodeURIComponent(title)}`;

        if (details) {
            url += `&details=${encodeURIComponent(details)}`;
        }

        if (location) {
            url += `&location=${encodeURIComponent(location)}`;
        }

        url += `&dates=${formatDateForGoogle(startDate)}%2F${formatDateForGoogle(endDate || startDate)}`;

        return url;
    });
};
